import { useMutation, useQueryClient } from '@tanstack/react-query';

import { IconButton } from '@components/icon-button';
import { IconAssign } from '@components/icons/assign';
import { IconEmail } from '@components/icons/email';
import { IconEmailRead } from '@components/icons/email-read';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { Menu } from '@components/menu';
import { useToaster } from '@components/toaster';

import { api } from 'api';
import { Message } from 'api/messages';
import { useModal } from 'features/modals';
import { useStore } from 'models/Provider';

export function MessageOptionsMenu({ row }: { row: Message }) {
  const { t } = useStore();
  const { showError, showSuccess } = useToaster();
  const { showModal } = useModal();
  const queryClient = useQueryClient();

  const markReadOrUnread = useMutation(
    ({ applicationId, status }: { applicationId: number; status: 'read' | 'unread' }) =>
      api.applications.read(applicationId, { messages: status === 'read' }),
    {
      onSuccess(_, { status }) {
        queryClient.invalidateQueries(['messages']);
        showSuccess(t(`messages.list.actions.mark${status}.success`));
      },
      onError(_, { status }) {
        showError(t(`messages.list.actions.mark${status}.error`));
      },
    },
  );

  return (
    <Menu
      trigger={IconButton}
      triggerProps={{
        icon: <IconMoreVertical />,
        variant: 'ghost-purple',
      }}
    >
      {row.unread ? (
        <Menu.Item
          label={t('messages.list.actions.markread')}
          icon={<IconEmail />}
          onSelect={() =>
            markReadOrUnread.mutate({ applicationId: row.applicationId, status: 'read' })
          }
        />
      ) : (
        <Menu.Item
          label={t('messages.list.actions.markunread')}
          icon={<IconEmailRead />}
          onSelect={() =>
            markReadOrUnread.mutate({ applicationId: row.applicationId, status: 'unread' })
          }
        />
      )}
      <Menu.Item
        label={t('messages.list.actions.assign')}
        icon={<IconAssign />}
        onSelect={() =>
          showModal('ASSIGN_APPLICANT', {
            applicationId: row.applicationId,
            applicationName: row.applicationName,
            applicationAssignedToId: row.applicationAssignedToId,
          })
        }
      />
    </Menu>
  );
}
